/**
 * Attribution utilities — reads UTM params from URL (sticky via sessionStorage)
 * and returns an object ready to attach to any lead-submission payload.
 *
 * Usage:
 *   import { getAttribution, buildLeadPayload } from '../utils/attribution';
 *   const payload = buildLeadPayload({ email, name }, { source: 'homepage_leadcapture' });
 *   axios.post('/api/leads', payload);
 */

const STORAGE_KEY = 'sb_attribution_v1';

/**
 * Captures UTM params from the current URL (if any) and persists them for the
 * rest of the session, so a lead submitted after navigation still carries
 * the original campaign source.
 */
export const captureAttributionFromURL = () => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const fresh = {};
  utmKeys.forEach((k) => {
    const v = params.get(k);
    if (v) fresh[k] = v;
  });

  // Referrer captured once (first page view)
  const ref = document.referrer && !document.referrer.includes(window.location.host)
    ? document.referrer
    : undefined;

  const existing = getAttribution();
  const merged = {
    ...existing,
    ...fresh,
    ...(ref && !existing.referrer ? { referrer: ref } : {}),
  };

  if (Object.keys(fresh).length > 0 || (ref && !existing.referrer)) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (err) {
      // sessionStorage may be unavailable (private browsing / disabled storage).
      // Attribution is best-effort, never block the page render.
      console.warn('Attribution sessionStorage write failed:', err?.message || err);
    }
  }
};

export const getAttribution = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.warn('Attribution sessionStorage read failed:', err?.message || err);
    return {};
  }
};

/**
 * Builds the payload sent to POST /api/leads, merging the caller's fields
 * with any captured attribution data and the provided source tag.
 */
export const buildLeadPayload = (leadFields = {}, { source, tags } = {}) => {
  const attribution = getAttribution();
  return {
    ...leadFields,
    ...(source ? { source } : {}),
    ...(tags && tags.length ? { tags } : {}),
    ...(attribution.utm_source ? { utm_source: attribution.utm_source } : {}),
    ...(attribution.utm_medium ? { utm_medium: attribution.utm_medium } : {}),
    ...(attribution.utm_campaign ? { utm_campaign: attribution.utm_campaign } : {}),
    ...(attribution.utm_content ? { utm_content: attribution.utm_content } : {}),
    ...(attribution.utm_term ? { utm_term: attribution.utm_term } : {}),
    ...(attribution.referrer ? { referrer: attribution.referrer } : {}),
  };
};
